/**
 * Widget Wrapper Component
 *
 * Base wrapper for all dashboard widgets providing:
 * - Header with title and controls
 * - Edit mode selection highlight
 * - Refresh functionality
 * - Loading and error states
 * - Resize overlay in edit mode
 *
 * @author BizClick WMS
 * @version 2.0.0
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  RefreshCw,
  X,
  GripVertical,
  Move,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { WidgetInstance } from '../types';
import { getWidgetById } from '../widgetRegistry';
import { useDashboard } from '../DashboardContext';
import ResizeOverlay from './ResizeOverlay';
import { REFRESH_CHOICES, effectiveRefreshSeconds, formatInterval } from '../utils/refreshSettings';

/**
 * Når widgetens data sist ble hentet. Leses fra spørringsbufferen (nøkkel ['dashboard', …, instanceId]),
 * så hver widget slipper å sende tidspunktet inn selv.
 */
function useLastUpdated(instanceId: string): number | undefined {
  const queryClient = useQueryClient();
  const read = useCallback(() => {
    const latest = queryClient.getQueryCache()
      .findAll({ predicate: (q) => q.queryKey[0] === 'dashboard' && q.queryKey[2] === instanceId })
      .reduce((max, q) => Math.max(max, q.state.dataUpdatedAt), 0);
    return latest || undefined;
  }, [queryClient, instanceId]);
  const [updatedAt, setUpdatedAt] = useState<number | undefined>(read);
  useEffect(() => queryClient.getQueryCache().subscribe(() => setUpdatedAt(read())), [queryClient, read]);
  return updatedAt;
}

interface WidgetWrapperProps {
  instance: WidgetInstance;
  children: React.ReactNode;
  onRefresh?: () => void;
  isLoading?: boolean;
  error?: string | null;
  /** Whether this widget is connected to a live data source (not mock) */
  isLive?: boolean;
}

const WidgetWrapper: React.FC<WidgetWrapperProps> = ({
  instance,
  children,
  onRefresh,
  isLoading = false,
  error = null,
  isLive = false,
}) => {
  const { t } = useTranslation();
  const { state, removeWidget, setSelectedWidget, updateWidgetSettings } = useDashboard();
  const { isEditMode, selectedWidgetId, currentLayout } = state;
  const lastUpdated = useLastUpdated(instance.instanceId);
  const intervalSeconds = effectiveRefreshSeconds(instance, currentLayout);
  // «Følg dashboardet» viser hva det betyr for akkurat denne widgeten
  const dashboardSeconds = typeof currentLayout.refreshInterval === 'number' ? currentLayout.refreshInterval : instance.settings.refreshInterval;
  const widgetDef = getWidgetById(instance.widgetId);

  const [showResizeOverlay, setShowResizeOverlay] = useState(false);
  const isSelected = selectedWidgetId === instance.instanceId;

  // Translated widget name (keyed by stable widget id, falls back to registry Norwegian name)
  const translatedName = widgetDef
    ? t(`manager.modules.wmsDashboard.widgets.${widgetDef.id}.name`, {}, widgetDef.name)
    : t('manager.modules.wmsDashboard.widget.defaultTitle', {}, 'Widget');
  const title = instance.settings.title || translatedName;
  const showHeader = instance.settings.showHeader;

  // NOTE: Auto-refresh logic has been removed from WidgetWrapper.
  // Refresh is now handled by TanStack Query (React Query) via useWidgetData hook.
  // This eliminates timer-leak risks and provides better caching/retry behavior.

  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      onRefresh();
    }
  }, [onRefresh]);

  const handleRemove = useCallback(() => {
    removeWidget(instance.instanceId);
  }, [removeWidget, instance.instanceId]);

  const handleSelect = useCallback(() => {
    if (isEditMode) {
      setSelectedWidget(isSelected ? null : instance.instanceId);
    }
  }, [isEditMode, isSelected, setSelectedWidget, instance.instanceId]);

  const handleResizeClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowResizeOverlay(true);
  }, []);

  return (
    <>
      <div
        className={`
          relative h-full w-full flex flex-col
          bg-gray-800 rounded-lg overflow-hidden
          border-2 transition-all duration-200
          ${isEditMode ? 'cursor-grab active:cursor-grabbing' : ''}
          ${isSelected ? 'border-blue-500 ring-2 ring-blue-500/30' : 'border-gray-700'}
          ${isEditMode && !isSelected ? 'hover:border-gray-500' : ''}
        `}
        onClick={handleSelect}
      >
        {/* Header */}
        {showHeader && (
          <div className="flex items-center justify-between px-3 py-2 bg-gray-700/50 border-b border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              {isEditMode && (
                <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
              )}
              <h3 className="text-sm font-semibold text-white truncate">{title}</h3>
              {/* Live data indicator - subtle pulsing green dot when connected to real API */}
              {isLive && (
                <span
                  className="relative flex h-2 w-2 flex-shrink-0"
                  title={t('manager.modules.wmsDashboard.widget.liveDataConnected', {}, 'Tilkoblet live data')}
                >
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
              )}
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Sist oppdatert — viser at dataene er ferske (og om automatisk oppdatering er av) */}
              {!isEditMode && lastUpdated && (
                <span className="text-[11px] text-gray-400 whitespace-nowrap mr-1">
                  {/* Smale widgeter: bare klokkeslettet, så tittelen får plass */}
                  {instance.size.width <= 3
                    ? new Date(lastUpdated).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })
                    : t('manager.modules.wmsDashboard.refresh.updatedAt', { '0': new Date(lastUpdated).toLocaleTimeString('nb-NO') },
                      `Oppdatert ${new Date(lastUpdated).toLocaleTimeString('nb-NO')}`)}
                  {intervalSeconds <= 0 && ` · ${t('manager.modules.wmsDashboard.refresh.autoOff', {}, 'auto av')}`}
                </span>
              )}

              {/* Refresh button */}
              {onRefresh && !isEditMode && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRefresh();
                  }}
                  className="p-1 text-gray-400 hover:text-white hover:bg-gray-600 rounded transition-colors"
                  title={t('manager.modules.wmsDashboard.widget.refreshNow', {}, 'Oppdater nå')}
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
              )}

              {/* Edit mode controls */}
              {isEditMode && (
                <>
                  {/* Oppdatering for denne widgeten (overstyrer dashboardets valg) */}
                  <select
                    value={typeof instance.settings.refreshOverride === 'number' ? String(instance.settings.refreshOverride) : ''}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      const value = e.target.value;
                      updateWidgetSettings(instance.instanceId, { refreshOverride: value === '' ? undefined : Number(value) });
                    }}
                    className={`text-[11px] bg-gray-800 text-gray-300 border border-gray-600 rounded px-1 py-0.5 ${instance.size.width <= 2 ? "max-w-[4.5rem]" : "max-w-[8rem]"}`}
                    aria-label={t('manager.modules.wmsDashboard.refresh.widgetLabel', {}, 'Oppdatering for denne widgeten')}
                  >
                    <option value="">
                      {t('manager.modules.wmsDashboard.refresh.followDashboard', { '0': formatInterval(dashboardSeconds, t) },
                        `Følg dashboard (${formatInterval(dashboardSeconds, t)})`)}
                    </option>
                    {REFRESH_CHOICES.map((sec) => (
                      <option key={sec} value={sec}>{formatInterval(sec, t)}</option>
                    ))}
                  </select>
                  {/* Resize button */}
                  <button
                    onClick={handleResizeClick}
                    className="p-1 text-gray-400 hover:text-blue-400 hover:bg-gray-600 rounded transition-colors"
                    title={t('manager.modules.wmsDashboard.resize.title', {}, 'Endre størrelse')}
                  >
                    <Move className="w-3.5 h-3.5" />
                  </button>
                  {/* Remove button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove();
                    }}
                    className="p-1 text-gray-400 hover:text-red-400 hover:bg-gray-600 rounded transition-colors"
                    title={t('manager.modules.wmsDashboard.widget.removeWidget', {}, 'Fjern widget')}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 p-3 overflow-auto min-h-0">
          {error ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center text-red-400">
                <p className="text-sm">{t('manager.modules.wmsDashboard.widget.loadError', {}, 'Feil ved lasting')}</p>
                <p className="text-xs text-gray-500 mt-1">{error}</p>
                {onRefresh && (
                  <button
                    onClick={handleRefresh}
                    className="mt-2 px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded transition-colors"
                  >
                    {t('manager.modules.wmsDashboard.widget.tryAgain', {}, 'Prøv igjen')}
                  </button>
                )}
              </div>
            </div>
          ) : (
            children
          )}
        </div>

        {/* Size indicator and resize button in edit mode */}
        {isEditMode && (
          <button
            onClick={handleResizeClick}
            className="absolute bottom-1 right-1 px-2 py-0.5 text-[10px] text-gray-400 bg-gray-700/90 hover:bg-blue-600 hover:text-white rounded transition-colors cursor-pointer"
            title={t('manager.modules.wmsDashboard.widget.clickToResize', {}, 'Klikk for å endre størrelse')}
          >
            {instance.size.width} x {instance.size.height}
          </button>
        )}

        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-gray-800/50 flex items-center justify-center">
            <RefreshCw className="w-6 h-6 text-blue-400 animate-spin" />
          </div>
        )}
      </div>

      {/* Resize Overlay Modal */}
      {showResizeOverlay && (
        <ResizeOverlay
          instance={instance}
          onClose={() => setShowResizeOverlay(false)}
        />
      )}
    </>
  );
};

export default WidgetWrapper;
